// src/components/ui/LanguageSwitcher.jsx
import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import { Globe, ChevronDown, Check, X } from 'lucide-react';
import { doc, updateDoc } from 'firebase/firestore';
import { db, auth } from '../../firebase';
import { loadLocale, isSupportedLang } from '../../i18n';

const LANGUAGES = [
  { code: 'en', name: 'English', flag: '🇺🇸', short: 'EN', nativeName: 'English' },
  { code: 'de', name: 'Deutsch', flag: '🇩🇪', short: 'DE', nativeName: 'Deutsch' },
  { code: 'fr', name: 'Français', flag: '🇫🇷', short: 'FR', nativeName: 'Français' },
  { code: 'es', name: 'Español', flag: '🇪🇸', short: 'ES', nativeName: 'Español' },
  { code: 'tr', name: 'Türkçe', flag: '🇹🇷', short: 'TR', nativeName: 'Türkçe' },
  { code: 'ru', name: 'Русский', flag: '🇷🇺', short: 'RU', nativeName: 'Русский' }
];

const LanguageSwitcher = ({
  className = '',
  mode = 'dropdown', // 'dropdown' or 'modal'
  isOpen: externalIsOpen,
  onClose,
  onLanguageSelect
}) => {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  const currentLanguage = LANGUAGES.find(lang => lang.code === i18n.language) || LANGUAGES[0];

  // Use external isOpen for modal mode, internal for dropdown mode
  const isModalOpen = mode === 'modal' ? externalIsOpen : isOpen;

  useEffect(() => {
    if (mode === 'dropdown') {
      const handleClickOutside = (event) => {
        if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
          setIsOpen(false);
        }
      };

      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [mode]);

  const handleLanguageChange = async (languageCode) => {
    try {
      // Load locale if not yet loaded (lazy-loaded locales)
      await loadLocale(languageCode);
      await i18n.changeLanguage(languageCode);

      // Save to localStorage for quick access
      localStorage.setItem('logami-language', languageCode);
      localStorage.setItem('preferredLanguage', languageCode);

      // Save to Firebase if user is logged in
      const user = auth.currentUser;
      if (user) {
        const userRef = doc(db, 'users', user.uid);
        await updateDoc(userRef, {
          'preferences.language': languageCode
        });
        console.log(`Language preference saved to Firebase: ${languageCode}`);
      }

      // Keep URL in sync: same path but with new :lang (e.g. /en/dashboard -> /de/dashboard)
      const segments = location.pathname.split('/').filter(Boolean);
      const firstIsLang = segments[0] && isSupportedLang(segments[0]);
      const pathWithoutLang = firstIsLang ? '/' + segments.slice(1).join('/') : '/';
      const newPath = pathWithoutLang === '/' ? `/${languageCode}` : `/${languageCode}${pathWithoutLang}`;
      navigate(newPath + location.search + location.hash, { replace: true });

      if (mode === 'dropdown') {
        setIsOpen(false);
      } else if (onLanguageSelect) {
        onLanguageSelect(languageCode);
      }
    } catch (error) {
      console.error('Error changing language:', error);
      if (mode === 'dropdown') {
        setIsOpen(false);
      } else if (onClose) {
        onClose();
      }
    }
  };

  // Dropdown Mode (existing functionality)
  if (mode === 'dropdown') {
    return (
      <div className={`relative ${className}`} ref={dropdownRef}>
        <button
  ref={dropdownRef}
  onClick={() => setIsOpen(!isOpen)}
  style={{ position: 'relative', cursor: 'pointer' }}
  className="flex items-center gap-2 px-3 py-2 bg-transparent transition-all duration-300 touch-manipulation min-h-[44px] group"
  onMouseEnter={(e) => {
    e.currentTarget.querySelector('.lang-icon').style.color = '#3A86FF';
    e.currentTarget.querySelector('.lang-text').style.color = '#3A86FF';
    e.currentTarget.querySelector('.lang-chevron').style.color = '#3A86FF';
    e.currentTarget.querySelector('.lang-underline').style.width = '100%';
  }}
  onMouseLeave={(e) => {
    if (!isOpen) {
      e.currentTarget.querySelector('.lang-icon').style.color = '#000';
      e.currentTarget.querySelector('.lang-text').style.color = '#000';
      e.currentTarget.querySelector('.lang-chevron').style.color = '#000';
      e.currentTarget.querySelector('.lang-underline').style.width = '0';
    }
  }}
>
  <Globe className="lang-icon w-4 h-4 transition-colors duration-300" style={{ color: '#000' }} />
  <span className="lang-text font-semibold text-sm transition-colors duration-300" style={{ color: '#000' }}>
    {currentLanguage.short}
  </span>
  <ChevronDown
    className={`lang-chevron w-4 h-4 transition-all duration-300 ${isOpen ? 'rotate-180' : ''}`}
    style={{ color: '#000' }}
  />
  {/* Animated underline */}
  <span
    className="lang-underline"
    style={{
      position: 'absolute',
      left: '0',
      bottom: '0',
      width: isOpen ? '100%' : '0',
      height: '2px',
      backgroundColor: '#3A86FF',
      transition: 'width 0.3s ease'
    }}
  />
</button>

      {isOpen && (
  <div className="absolute right-0 top-full mt-2 w-48 bg-white rounded-lg shadow-xl border-2 border-black/10 py-1 z-50">
    {LANGUAGES.map((language) => {
      const isActive = i18n.language === language.code;
      return (
        <button
          key={language.code}
          onClick={() => handleLanguageChange(language.code)}
          style={{ position: 'relative', cursor: 'pointer' }}
          className="w-full text-left px-4 py-2 transition-colors"
          onMouseEnter={(e) => {
            if (!isActive) {
              e.currentTarget.querySelector('.lang-name').style.color = '#3A86FF';
              e.currentTarget.querySelector('.lang-menu-underline').style.width = '100%';
            }
          }}
          onMouseLeave={(e) => {
            if (!isActive) {
              e.currentTarget.querySelector('.lang-name').style.color = '#000';
              e.currentTarget.querySelector('.lang-menu-underline').style.width = '0';
            }
          }}
        >
          <div className="flex items-center gap-3">
            <span className="text-lg">{language.flag}</span>
            <span
              className="lang-name font-medium transition-colors duration-300 relative inline-block"
              style={{ color: isActive ? '#3A86FF' : '#000' }}
            >
              {language.name}
              {/* Hover underline - positioned under the text only */}
              {!isActive && (
                <span
                  className="lang-menu-underline"
                  style={{
                    position: 'absolute',
                    left: '0',
                    right: '0',
                    bottom: '-2px',
                    width: '0',
                    height: '2px',
                    backgroundColor: '#3A86FF',
                    transition: 'width 0.3s ease'
                  }}
                />
              )}
            </span>
            {isActive && (
              <div className="ml-auto w-2 h-2 rounded-full bg-[#3A86FF]"></div>
            )}
          </div>
        </button>
      );
    })}
  </div>
)}
      </div>
    );
  }

  // Modal Mode (for first-time visitors)
  if (mode === 'modal' && !isModalOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 z-[100] transition-opacity animate-fade-in"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="fixed inset-0 z-[101] flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 animate-scale-in">
          {/* Close Button */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-2 rounded-lg hover:bg-gray-100 transition-colors"
            aria-label="Close"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>

          {/* Header */}
          <div className="text-center mb-6">
            <div className="w-16 h-16 bg-yellow-400 rounded-full flex items-center justify-center mx-auto mb-4">
              <Globe className="w-8 h-8 text-black" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              Choose Your Language
            </h2>
            <p className="text-gray-600">
              Select your preferred language for the best experience
            </p>
          </div>

          {/* Language Options */}
          <div className="space-y-2 max-h-[400px] overflow-y-auto">
            {LANGUAGES.map((language) => (
              <button
                key={language.code}
                onClick={() => handleLanguageChange(language.code)}
                className={`w-full flex items-center justify-between p-4 rounded-xl border-2 transition-all hover:border-yellow-400 hover:bg-yellow-50 ${
                  i18n.language === language.code
                    ? 'border-yellow-400 bg-yellow-50'
                    : 'border-gray-200'
                }`}
              >
                <div className="flex items-center space-x-3">
                  <span className="text-3xl">{language.flag}</span>
                  <div className="text-left">
                    <p className="font-semibold text-gray-900">{language.nativeName}</p>
                    <p className="text-sm text-gray-500">{language.name}</p>
                  </div>
                </div>
                {i18n.language === language.code && (
                  <Check className="w-5 h-5 text-yellow-600" />
                )}
              </button>
            ))}
          </div>

          {/* Footer Note */}
          <p className="text-xs text-gray-500 text-center mt-6">
            You can change the language anytime in settings
          </p>
        </div>
      </div>
    </>
  );
};

export default LanguageSwitcher;